import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { AgentCancelledError } from "./runner/approval";
import { MIN_THINKING_MS, openCount, runAgent } from "./runner/runAgent";
import type {
    AgentMode,
    AgentChoice,
    AgentContext,
    AgentIO,
    AgentMessage,
    AgentRunControl,
    AgentStep,
    AgentStepId,
    AgentStepReport,
    AgentVisualState,
    ReportLine,
} from "./runner/types";

/** `pausing`: a pause was asked for and takes effect once the running step returns. */
export type AgentRunState = "idle" | "running" | "pausing" | "paused" | "finished";

export interface AgentPending {
    choices: AgentChoice<string>[];
}

export interface AgentStepStateEntry {
    state: AgentVisualState;
    result?: ReportLine;
}

const REPORT_STATE: Record<AgentStepReport["status"], AgentVisualState> = {
    done: "done",
    nothing: "nothing",
    skipped: "skipped",
    failed: "failed",
};

const resultLine = (report: AgentStepReport): ReportLine | undefined => {
    if (report.status === "done") return report.summary[0];
    if (report.status === "nothing") {
        return report.open.length > 0
            ? { textId: "app.agent.stepList.open", values: { count: openCount(report.open) } }
            : { textId: "app.agent.stepList.nothing" };
    }
    if (report.status === "failed") return { textId: "app.agent.stepList.failed" };
    return { textId: report.skipReason === "noRight" ? "app.agent.stepList.noRight" : "app.agent.stepList.skipped" };
};

const withoutId = (set: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    if (!set.has(id)) {
        return set;
    }
    const next = new Set(set);
    next.delete(id);
    return next;
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Drives `runAgent` from React state; the run starts with `start()` and stops at the next step boundary on `cancel()`. */
export const useAgentRun = (steps: AgentStep[], context: AgentContext, initialMode: AgentMode = "approval") => {
    const [mode, setModeState] = useState<AgentMode>(initialMode);
    const modeRef = useRef<AgentMode>(initialMode);
    const [messages, setMessages] = useState<AgentMessage[]>([]);
    const [pending, setPending] = useState<AgentPending | null>(null);
    const [state, setState] = useState<AgentRunState>("idle");
    const [stepNumber, setStepNumber] = useState(0);
    // Bumped once the runner has recorded a step so stepStates re-reads the mutable run report.
    const [reportTick, setReportTick] = useState(0);
    // Panels whose own write (upload, bank import) is in flight.
    const [busyPanels, setBusyPanels] = useState<ReadonlySet<string>>(() => new Set());

    const startedRef = useRef(false);
    const cancelledRef = useRef(false);
    const pausedRef = useRef(false);
    const resumeRef = useRef<(() => void) | null>(null);
    const answerRef = useRef<((value: string) => void) | null>(null);
    const rejectAskRef = useRef<((reason: Error) => void) | null>(null);
    const panelRef = useRef(new Map<string, (result: "done" | "skipped") => void>());
    const counterRef = useRef(0);
    // The runner pieces of this run, kept so a rerun goes through the same io, control and numbering.
    const runnerRef = useRef<{
        io: Omit<AgentIO, "narrateSkipped" | "approve">;
        control: AgentRunControl;
        steps: AgentStep[];
    } | null>(null);
    // True while runAgent is walking steps (the first pass or a rerun).
    const walkingRef = useRef(false);
    const stepsRef = useRef(steps);
    const contextRef = useRef(context);
    stepsRef.current = steps;
    contextRef.current = context;

    const post = useCallback((m: Omit<AgentMessage, "id">) => {
        const id = `m${++counterRef.current}`;
        setMessages((prev) => [...prev, { ...m, id }]);
        return id;
    }, []);

    const update = useCallback((id: string, patch: Partial<AgentMessage>) => {
        setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    }, []);

    const start = useCallback(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        setState("running");

        const io: Omit<AgentIO, "narrateSkipped" | "approve"> = {
            post,
            update,
            ask: (textId, choices, values, options) =>
                new Promise((resolve, reject) => {
                    if (cancelledRef.current) {
                        reject(new AgentCancelledError());
                        return;
                    }
                    if (!options?.silent) post({ kind: "question", textId, values });
                    rejectAskRef.current = reject;
                    answerRef.current = (value) => {
                        const choice = choices.find((c) => c.value === value);
                        if (choice && !options?.silent) post({ kind: "user", textId: choice.labelId });
                        resolve(value as (typeof choices)[number]["value"]);
                    };
                    setPending({ choices });
                }),
            panel: (panel) =>
                new Promise((resolve) => {
                    if (cancelledRef.current) {
                        resolve("skipped");
                        return;
                    }
                    const id = post({
                        kind: "question",
                        textId: panel.type === "modeChoice" ? "app.agent.mode.question" : "app.agent.title",
                        panel,
                    });
                    panelRef.current.set(id, (result) => {
                        panelRef.current.delete(id);
                        setBusyPanels((prev) => withoutId(prev, id));
                        update(id, { panel: undefined });
                        resolve(result);
                    });
                }),
            think: async (textId) => {
                const id = post({ kind: "thinking", textId });
                await sleep(MIN_THINKING_MS);
                update(id, { kind: "info" });
            },
            isCancelled: () => cancelledRef.current,
            setMode: (m) => {
                modeRef.current = m;
                setModeState(m);
            },
        };
        const control: AgentRunControl = {
            isCancelled: () => cancelledRef.current,
            waitIfPaused: () => {
                if (!pausedRef.current) {
                    return Promise.resolve();
                }
                setState((s) => (s === "pausing" ? "paused" : s));
                return new Promise<void>((r) => (resumeRef.current = r));
            },
        };
        const numbered = stepsRef.current.map((step, index) => ({
            ...step,
            run: async (ctx: AgentContext, stepIo: AgentIO) => {
                setStepNumber(index + 1);
                try {
                    return await step.run(ctx, stepIo);
                } catch (error) {
                    // A question rejected by cancel ends the step quietly; the run loop then stops.
                    if (error instanceof AgentCancelledError) return { status: "failed" as const };
                    throw error;
                }
            },
        }));

        runnerRef.current = { io, control, steps: numbered };
        walkingRef.current = true;
        runAgent(
            numbered,
            contextRef.current,
            io,
            control,
            () => modeRef.current,
            () => setReportTick((n) => n + 1),
        )
            .catch((error: unknown) => console.error("Agent run failed", error))
            .finally(() => {
                walkingRef.current = false;
                setState("finished");
            });
    }, [post, update]);

    /** Runs one step again with the current mode, records it into the same report and rebuilds the report. */
    const rerunStep = useCallback((id: AgentStepId) => {
        const runner = runnerRef.current;
        if (!runner || walkingRef.current || cancelledRef.current || id === "report" || id === "start") {
            return;
        }
        const step = runner.steps.find((s) => s.id === id);
        if (!step) {
            return;
        }
        const reportStep = runner.steps.find((s) => s.id === "report");
        const ctx = contextRef.current;
        const report = ctx.runReport;
        delete report.steps[id];
        if (reportStep) {
            delete report.steps.report;
        }
        walkingRef.current = true;
        setMessages((prev) => prev.filter((m) => m.kind !== "report"));
        setState("running");
        runAgent(
            reportStep ? [step, reportStep] : [step],
            ctx,
            runner.io,
            runner.control,
            () => modeRef.current,
            () => setReportTick((n) => n + 1),
            report,
        )
            .catch((error: unknown) => console.error("Agent rerun failed", error))
            .finally(() => {
                walkingRef.current = false;
                setState("finished");
            });
    }, []);

    const setMode = useCallback(
        (m: AgentMode) => {
            if (m === modeRef.current) return;
            modeRef.current = m;
            setModeState(m);
            if (startedRef.current) {
                const label = m === "quick" ? "app.agent.mode.quickSwitch" : "app.agent.mode.approval";
                post({ kind: "info", textId: "app.agent.mode.nextStep", labels: { mode: label } });
            }
        },
        [post],
    );

    const answer = useCallback((value: string) => {
        const resolve = answerRef.current;
        answerRef.current = null;
        rejectAskRef.current = null;
        setPending(null);
        resolve?.(value);
    }, []);

    const pause = useCallback(() => {
        pausedRef.current = true;
        setState((s) => (s === "running" ? "pausing" : s));
    }, []);

    const resume = useCallback(() => {
        pausedRef.current = false;
        resumeRef.current?.();
        resumeRef.current = null;
        setState((s) => (s === "paused" || s === "pausing" ? "running" : s));
    }, []);

    const rejectPendingAsk = useCallback(() => {
        const reject = rejectAskRef.current;
        rejectAskRef.current = null;
        answerRef.current = null;
        setPending(null);
        reject?.(new AgentCancelledError());
    }, []);

    const cancel = useCallback(() => {
        cancelledRef.current = true;
        rejectPendingAsk();
        resume();
        panelRef.current.forEach((done) => done("skipped"));
    }, [resume, rejectPendingAsk]);

    const completePanel = useCallback(
        (id: string, result: "done" | "skipped") => panelRef.current.get(id)?.(result),
        [],
    );

    const setPanelBusy = useCallback((id: string, isBusy: boolean) => {
        setBusyPanels((prev) => {
            if (prev.has(id) === isBusy || (isBusy && !panelRef.current.has(id))) {
                return prev;
            }
            return isBusy ? new Set([...prev, id]) : withoutId(prev, id);
        });
    }, []);

    useEffect(
        () => () => {
            cancelledRef.current = true;
            rejectAskRef.current?.(new AgentCancelledError());
        },
        [],
    );

    const askingNow = pending !== null || messages.some((m) => m.panel !== undefined);
    const { stepStates, currentNumber } = useMemo(() => {
        const reports = context.runReport.steps;
        // A paused run points at the step that runs next once a finished step is behind it.
        let current = stepNumber;
        if (state === "paused") {
            const next = steps.findIndex((s, i) => i >= Math.max(stepNumber - 1, 0) && !reports[s.id]);
            if (next >= 0) {
                current = next + 1;
            }
        }
        const states = {} as Record<AgentStepId, AgentStepStateEntry>;
        for (const [index, step] of steps.entries()) {
            const report = reports[step.id];
            if (report) {
                const result = resultLine(report);
                states[step.id] = { state: REPORT_STATE[report.status], ...(result ? { result } : {}) };
            } else if (state !== "finished" && index + 1 === current) {
                states[step.id] = {
                    state: state === "paused" || state === "pausing" ? state : askingNow ? "asking" : "running",
                };
            } else {
                states[step.id] = { state: "pending" };
            }
        }
        return { stepStates: states, currentNumber: current };
        // oxlint-disable-next-line react/exhaustive-deps -- reportTick re-reads the mutable run report
    }, [steps, context, state, stepNumber, askingNow, reportTick]);
    const stepIds = useMemo(() => steps.map((s) => s.id), [steps]);
    const panelBusy = busyPanels.size > 0;
    // A server call is in flight: closing is locked until the agent waits for the user, has paused or finished.
    const busy = panelBusy || ((state === "running" || state === "pausing") && !askingNow);

    return useMemo(
        () => ({
            stepStates,
            stepIds,
            messages,
            pending,
            answer,
            pause,
            resume,
            cancel,
            start,
            completePanel,
            setPanelBusy,
            state,
            busy,
            panelBusy,
            askingNow,
            stepNumber: currentNumber,
            stepTotal: steps.length,
            mode,
            setMode,
            rerunStep,
        }),
        [
            stepStates,
            stepIds,
            messages,
            pending,
            answer,
            pause,
            resume,
            cancel,
            start,
            completePanel,
            setPanelBusy,
            state,
            busy,
            panelBusy,
            askingNow,
            currentNumber,
            steps.length,
            mode,
            setMode,
            rerunStep,
        ],
    );
};

export type AgentRun = ReturnType<typeof useAgentRun>;
